import React from 'react';

import { BaseText, isElement, isText, Props, SlateNode } from './SlateRenderer.types';

const SlateRenderer = ({ value }: Props) => {
  if (!value || !Array.isArray(value)) {
    return <div>No content to display</div>;
  }

  /**
   * Renders a Slate node based on its type
   * @param node - The Slate node to render
   * @param index - The index of the node in its parent's children array
   */
  const renderNode = (node: SlateNode, index: number): React.ReactNode => {
    // If this is a text node
    if (isText(node)) {
      return renderLeaf(node, index);
    }

    // For element nodes
    if (isElement(node)) {
      // Handle different node types
      switch (node.type) {
        case 'paragraph':
          return (
            <p key={index} className="slate-paragraph">
              {node.children.map((child, childIndex) => renderNode(child, childIndex))}
            </p>
          );
        case 'heading-one':
          return (
            <h1 key={index} className="slate-heading">
              {node.children.map((child, childIndex) => renderNode(child, childIndex))}
            </h1>
          );
        case 'heading-two':
          return (
            <h2 key={index} className="slate-heading">
              {node.children.map((child, childIndex) => renderNode(child, childIndex))}
            </h2>
          );
        case 'list-item':
          return (
            <li key={index} className="slate-list-item">
              {node.children.map((child, childIndex) => renderNode(child, childIndex))}
            </li>
          );
        case 'bulleted-list':
          return (
            <ul key={index} className="slate-list">
              {node.children.map((child, childIndex) => renderNode(child, childIndex))}
            </ul>
          );
        case 'numbered-list':
          return (
            <ol key={index} className="slate-list">
              {node.children.map((child, childIndex) => renderNode(child, childIndex))}
            </ol>
          );
        default:
          return (
            <div key={index} className="slate-block">
              {node.children.map((child, childIndex) => renderNode(child, childIndex))}
            </div>
          );
      }
    }

    // Fallback for unrecognized node types
    return null;
  };

  /**
   * Renders a text leaf with its formatting
   * @param leaf - The text leaf to render
   * @param index - The index of the leaf
   */
  const renderLeaf = (leaf: BaseText, index: number): React.ReactNode => {
    if (!leaf) return null;

    // Handle newlines in text by replacing them with <br> elements
    if (leaf.text.includes('\n')) {
      const segments = leaf.text.split('\n');

      return (
        <React.Fragment key={`leaf-${index}`}>
          {segments.map((segment, i) => {
            // Clone the leaf but with just this segment of text
            const segmentLeaf = { ...leaf, text: segment };

            // Return the formatted segment followed by a <br> if it's not the last segment
            return (
              <React.Fragment key={`segment-${i}`}>
                {i > 0 && <br />}
                {formatText(segmentLeaf, `${index}-${i}`)}
              </React.Fragment>
            );
          })}
        </React.Fragment>
      );
    }

    return formatText(leaf, index);
  };

  /**
   * Applies formatting to a text node
   * @param leaf - The text leaf to format
   * @param key - The key for React elements
   */
  const formatText = (leaf: BaseText, key: string | number): React.ReactNode => {
    let text: React.ReactNode = leaf.text || '';

    // Apply text formatting
    if (leaf.bold) {
      text = <strong key={`bold-${key}`}>{text}</strong>;
    }
    if (leaf.italic) {
      text = <em key={`italic-${key}`}>{text}</em>;
    }
    if (leaf.underline) {
      text = <u key={`underline-${key}`}>{text}</u>;
    }
    if (leaf.code) {
      text = <code key={`code-${key}`}>{text}</code>;
    }

    return text;
  };

  return <div>{value.map((node, index) => renderNode(node, index))}</div>;
};

export default SlateRenderer;
